// Platform-admin directory — shared mechanism (decision 2026-07-04): the admin
// list is `platform_config/admins.emails: string[]` in EACH app's own Firebase
// project (separate auth pools, same pattern), not a custom claim.
// Granting/revoking = editing one Firestore doc; no Admin-SDK script, no token
// refresh. Each app instantiates the repository with its own `db` via
// `createPlatformAdminRepository`.
import { doc, getDoc, type DocumentSnapshot, type Firestore } from 'firebase/firestore'
import { logger } from './logger'

const PLATFORM_CONFIG = 'platform_config'
const ADMINS_DOC = 'admins'

// `platform_config/admins` is readable by ANY authenticated user (firestore
// rules: `match /platform_config/{docId} { allow read: if request.auth !=
// null }`), so the only way the read fails is a transient `permission-denied` /
// `unauthenticated` while the Firebase Auth token is still attaching to the
// Firestore client on a cold page load — a window that clears in well under a
// second. Swallowing THAT as "not admin" bounces a real admin off the admin
// surface on first paint (it self-heals on refresh). So retry the read across
// the hydration window before giving up; a genuine non-admin never lands here
// (their read succeeds and returns a clean `false`). Terminal failure still
// fails closed — see `isAdmin`'s catch.
const TRANSIENT_READ_CODES = new Set(['permission-denied', 'unauthenticated', 'unavailable'])
const ADMINS_READ_MAX_RETRIES = 3
const ADMINS_READ_RETRY_MS = 250

async function readAdminsDoc(db: Firestore): Promise<DocumentSnapshot> {
  for (let attempt = 0; ; attempt++) {
    try {
      return await getDoc(doc(db, PLATFORM_CONFIG, ADMINS_DOC))
    } catch (e) {
      const code = (e as { code?: string } | null)?.code ?? ''
      if (attempt >= ADMINS_READ_MAX_RETRIES || !TRANSIENT_READ_CODES.has(code)) {
        throw e
      }
      // Linear backoff (250/500/750ms) — spans the sub-second hydration race
      // without a long tail when the token genuinely never attaches.
      await new Promise((resolve) => setTimeout(resolve, ADMINS_READ_RETRY_MS * (attempt + 1)))
    }
  }
}

/**
 * Platform admin directory: looks up `platform_config/admins` and checks
 * whether the given email is listed.
 *
 * Supports both formats:
 * - `{ emails: ["a@x.com", "b@x.com"] }` (current)
 * - `{ "a@x.com": true, "b@x.com": true }` (legacy map-style)
 */
export class PlatformAdminRepository {
  constructor(private readonly db: Firestore) {}

  async isAdmin(email: string | null | undefined): Promise<boolean> {
    if (!email) return false
    try {
      const snap = await readAdminsDoc(this.db)
      if (!snap.exists()) return false
      const data = snap.data()
      if (!data) return false
      if (Array.isArray(data['emails'])) {
        return (data['emails'] as unknown[]).includes(email)
      }
      return Object.prototype.hasOwnProperty.call(data, email)
    } catch (e) {
      // Fail closed: a read that never succeeds (even after retries) yields
      // non-admin, never admin — so an outage can't grant access.
      logger.w('platformAdmin.isAdmin failed after retries — treating as non-admin', e)
      return false
    }
  }

  /**
   * List every platform-admin email. Read-only directory for admin-panel
   * "Administradores" surfaces. Handles both the current array form and the
   * legacy `{ email: true }` map. Returns [] on any failure (callers degrade
   * to an empty state rather than throwing).
   */
  async list(): Promise<string[]> {
    try {
      const snap = await readAdminsDoc(this.db)
      if (!snap.exists()) return []
      const data = snap.data()
      if (!data) return []
      if (Array.isArray(data['emails'])) {
        return (data['emails'] as unknown[]).filter((e): e is string => typeof e === 'string')
      }
      // Legacy map-style { "a@x.com": true } — keep only truthy entries.
      return Object.keys(data).filter((k) => data[k] === true)
    } catch (e) {
      logger.w('platformAdmin.list failed', e)
      return []
    }
  }
}

/** Bind the shared repository to an app's Firestore instance. */
export function createPlatformAdminRepository(db: Firestore): PlatformAdminRepository {
  return new PlatformAdminRepository(db)
}
